/**
 * Damage analysis UI for a single reservation.
 *
 * Route: /svc/reservation/:reservationId/damage
 *
 * Flow:
 *   - On mount: fetch availability + latest report.
 *   - If canRun = false → show explanation (missing handover/return/photos).
 *   - If report exists → show it. "Spustit znovu" reruns analysis.
 *   - If no report → show "Spustit analýzu" button + photo counts.
 *   - During run → spinner + "Analyzuji…".
 *   - On error → red banner with the structured error code.
 */

import { useState } from 'react'
import { useParams } from 'react-router'
import {
	DamageDebug,
	DamageItem,
	DamageReport,
	useDamageAnalysisAvailability,
	useDamageAnalysisRun,
	useDamageFeedback,
	useDamageReport,
} from '../api/damage-analysis'
import { Button } from '../components/button'
import { LOADING } from '../components/loading'
import { Paper } from '../components/paper'
import { DamagePhotoViewer } from './damage-photo-viewer'

const SEVERITY_CHIP: Record<1 | 2 | 3, string> = {
	1: 'bg-yellow-100 text-yellow-800',
	2: 'bg-orange-100 text-orange-800',
	3: 'bg-red-100 text-red-800',
}

const TYPE_LABEL: Record<DamageItem['type'], string> = {
	scratch: 'Škrábanec',
	dent: 'Důlek',
	scuff: 'Oder',
	crack: 'Prasklina',
	chip: 'Odštípnutí',
	missing_part: 'Chybějící díl',
	dirt_stain: 'Skvrna',
	other: 'Jiné',
}

const ANGLE_LABEL: Record<NonNullable<DamageItem['angle']>, string> = {
	front: 'přední',
	left_side: 'levý bok',
	rear: 'zadní',
	right_side: 'pravý bok',
	interior: 'interiér',
	trunk: 'kufr',
	extra: 'detail',
	'': '',
}

const SEVERITY_LABEL: Record<1 | 2 | 3, string> = {
	1: 'drobné',
	2: 'znatelné',
	3: 'vážné',
}

// Mapování ServerError kódů ze server/class/server-error.js
// na uživatelské hlášky. Pokud server přidá nový kód a my ho zde
// nemáme, UI spadne na server.message (taky česky).
const ERROR_HUMAN: Record<string, string> = {
	DAMAGE_INVALID_RESERVATION_ID: 'Neplatné ID rezervace.',
	DAMAGE_NO_HANDOVER_REQUEST: 'Záznam o výdeji ještě nebyl vytvořen.',
	DAMAGE_NO_RETURN_REQUEST: 'Záznam o vratce ještě nebyl vytvořen.',
	DAMAGE_NO_BEFORE_PHOTOS: 'Výdej neobsahuje žádné fotky vozidla.',
	DAMAGE_NO_AFTER_PHOTOS: 'Vratka neobsahuje žádné fotky vozidla.',
	DAMAGE_IMAGE_FETCH_FAILED:
		'Žádnou z fotek se nepodařilo stáhnout. Zkontroluj, že jsou dostupné.',
	DAMAGE_PAYLOAD_TOO_LARGE:
		'Fotky jsou i po zmenšení příliš velké pro Gemini. Sniž jejich počet nebo rozlišení.',
	DAMAGE_NO_API_KEY:
		'Není nakonfigurován Gemini API klíč. Vlož ho přes proměnnou GEMINI_API_KEY.',
	GEMINI_ALL_MODELS_QUOTA_EXCEEDED:
		'Všechny modely vyčerpaly free-tier kvótu. Doplň placený billing nebo počkej.',
	GEMINI_ALL_MODELS_FAILED: 'Všechny Gemini modely selhaly. Zkontroluj logy serveru.',
	GEMINI_BLOCKED_BY_SAFETY: 'Gemini zablokoval analýzu safety filtrem.',
	GEMINI_EMPTY_TEXT: 'Gemini vrátil prázdnou odpověď.',
	GEMINI_UNPARSEABLE_RESULT: 'Odpověď Gemini se nepodařilo rozparsovat jako JSON.',
	BAD_REQUEST: 'Vnitřní chyba serveru. Mrkni do logů.',
}

export function ReservationDamageAnalysisRoute() {
	const { reservationId = '' } = useParams()
	const availability = useDamageAnalysisAvailability(reservationId)
	const report = useDamageReport(reservationId)
	const run = useDamageAnalysisRun(reservationId)
	const [multiPass, setMultiPass] = useState(false)

	if (availability.isLoading) {
		return (
			<Paper className="m-4 p-6">
				<div className="flex justify-center">{LOADING}</div>
			</Paper>
		)
	}

	const av = availability.data

	if (!av || !av.canRun) {
		return (
			<Paper className="m-4 p-5">
				<h4 className="text-base font-bold text-slate-800 mb-2">Vyhodnotit poškození</h4>
				<p className="text-sm text-slate-600">
					{!av?.hasHandover
						? 'Pro tuto rezervaci ještě nebyl odeslán záznam o výdeji vozidla.'
						: !av?.hasReturn
							? 'Pro tuto rezervaci ještě nebyl odeslán záznam o vratce.'
							: 'Záznamy existují, ale chybí v nich fotky vozidla. Pokladna musí auto nafotit při výdeji i vrácení.'}
				</p>
				<dl className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-500 max-w-md">
					<dt>Výdej</dt>
					<dd>{av?.hasHandover ? `✓ ${av.beforePhotoCount} fotek` : '✗ chybí'}</dd>
					<dt>Vratka</dt>
					<dd>{av?.hasReturn ? `✓ ${av.afterPhotoCount} fotek` : '✗ chybí'}</dd>
				</dl>
			</Paper>
		)
	}

	const errCode = (run.error as { code?: string } | null)?.code
	const errMsg = (run.error as { message?: string } | null)?.message
	const errDisplay = errCode ? (ERROR_HUMAN[errCode] ?? errMsg ?? errCode) : null
	// On a failed run the server attaches the debug payload to ServerError.extra
	// (see server/class/server-error.js + client/fetch.ts).
	const errorDebug =
		(run.error as { extra?: { debug?: DamageDebug } } | null)?.extra?.debug ?? null

	const reportData = report.data ?? null

	return (
		<div className="m-4 space-y-4">
			<Paper className="p-5">
				<div className="flex items-start justify-between gap-4 flex-wrap">
					<div>
						<h4 className="text-base font-bold text-slate-800">Vyhodnotit poškození</h4>
						<p className="mt-1 text-xs text-slate-500">
							Porovná {av.beforePhotoCount} fotek z výdeje a {av.afterPhotoCount}{' '}
							fotek z vratky pomocí Google Gemini a vypíše nová poškození.
						</p>
					</div>
					<div className="flex items-center gap-2">
						{run.isPending ? (
							<span className="inline-flex items-center gap-2 text-sm text-slate-600">
								<span className="inline-block w-4 h-4 border-2 border-slate-300 border-t-blue-600 rounded-full animate-spin" />
								Analyzuji… (20–60 s)
							</span>
						) : null}
						<label
							className="text-xs text-slate-600 inline-flex items-center gap-1.5 cursor-pointer"
							title="Spustí analýzu 2× s různými parametry a vrátí jen shody — pomalejší, ale spolehlivější."
						>
							<input
								type="checkbox"
								checked={multiPass}
								disabled={run.isPending}
								onChange={e => setMultiPass(e.target.checked)}
							/>
							2× kontrola
						</label>
						<Button
							variant="PRIMARY"
							size="MEDIUM"
							isDisabled={run.isPending}
							onPress={() => run.mutate({ minConfidence: 0, multiPass })}
						>
							{reportData ? 'Spustit znovu' : 'Spustit analýzu'}
						</Button>
					</div>
				</div>
				{errDisplay ? (
					<div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
						<strong>Chyba:</strong> {errDisplay}
						{errCode ? (
							<span className="ml-2 text-xs text-red-500">
								<code>{errCode}</code>
							</span>
						) : null}
					</div>
				) : null}
			</Paper>

			{/* Debug z neúspěšného běhu — ukáže, kde to spadlo. */}
			{errorDebug ? <DebugPanel debug={errorDebug} /> : null}

			{report.isLoading ? (
				<Paper className="p-6">
					<div className="flex justify-center">{LOADING}</div>
				</Paper>
			) : reportData ? (
				<DamageReportView report={reportData} />
			) : (
				<Paper className="p-5">
					<p className="text-sm text-slate-500">
						Pro tuto rezervaci ještě analýza neběžela. Klikni na{' '}
						<em>Spustit analýzu</em>.
					</p>
				</Paper>
			)}
		</div>
	)
}

function DamageReportView({ report }: { report: DamageReport }) {
	const { reservationId = '' } = useParams()
	const damages = report.damages || []
	const fallback = report.model_fallback_used
	const damageCount = damages.length
	const hasBboxes = damages.some(d => !!d.bbox)
	const hasPhotos = !!report.after_photos?.length

	// Two coordinated states: hover (mouse), active (click).
	// Hover paints the box on photo; click scrolls photo into view and
	// also scrolls the card. Same indices on both sides.
	const [hoveredIdx, setHoveredIdx] = useState<number | null>(null)
	const [activeIdx, setActiveIdx] = useState<number | null>(null)

	// Toggle: if there are no bboxes, hide the viewer by default to save
	// space. User can still flip it back on.
	const [showViewer, setShowViewer] = useState<boolean>(hasPhotos)

	// Per-damage feedback state. Tracks which damages the operator has
	// rejected/confirmed in this session so the UI can reflect that
	// without re-fetching. Backend stores it as activity_log entries.
	const [feedbackState, setFeedbackState] = useState<Record<number, 'reject' | 'confirm'>>({})
	const feedback = useDamageFeedback(reservationId)

	return (
		<>
			<Paper className="p-5">
				<div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 mb-2">
					<span>
						Model: <code className="text-slate-700">{report.model}</code>
					</span>
					{fallback ? (
						<span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-800">
							fallback z {report.model_primary}
						</span>
					) : null}
					{report.multi_pass_used ? (
						<span
							className="rounded-full bg-indigo-100 px-2 py-0.5 text-indigo-800"
							title={
								report.multi_pass_dropped
									? `Druhý průchod zahodil ${report.multi_pass_dropped} potenciálně halucinovaných položek.`
									: 'Druhý průchod potvrdil všechny detekce.'
							}
						>
							multi-pass
							{report.multi_pass_dropped ? ` (−${report.multi_pass_dropped})` : ''}
						</span>
					) : null}
					<span>·</span>
					<span>
						Porovnáno {report.counts.before_loaded} × {report.counts.after_loaded} fotek
					</span>
					<span>·</span>
					<span>{Math.round(report.duration_ms / 100) / 10}s</span>
					<span>·</span>
					<span>
						Vyhodnoceno{' '}
						{report.created_at ? formatDateTime(report.created_at) : 'neuvedeno'}
					</span>
				</div>
				<p className="text-base font-medium text-slate-800">
					{report.summary || '(bez shrnutí)'}
				</p>
				{report.model_attempts.length > 1 ? (
					<details className="mt-2">
						<summary className="cursor-pointer text-xs text-slate-400">
							log pokusů ({report.model_attempts.length})
						</summary>
						<ul className="mt-1 space-y-0.5 text-xs text-slate-500">
							{report.model_attempts.map((a, idx) => (
								<li key={idx}>
									• <code>{a.model}</code> (key #{a.key_index}) → {a.status}
									{a.reason ? ` ${a.reason}` : ''}
								</li>
							))}
						</ul>
					</details>
				) : null}
			</Paper>

			{report.voice_transcripts && report.voice_transcripts.length > 0 ? (
				<Paper className="p-4">
					<div className="flex items-center gap-2 mb-2">
						<span className="text-base">🎤</span>
						<h5 className="text-sm font-bold text-slate-700">Hlasové poznámky</h5>
					</div>
					<div className="space-y-3">
						{report.voice_transcripts.map((vt, i) => (
							<div key={i} className="border-l-2 border-slate-200 pl-3">
								<div className="flex items-center gap-2 mb-1 text-xs text-slate-500">
									<span
										className={`rounded-full px-2 py-0.5 ${
											vt.source === 'handover'
												? 'bg-blue-50 text-blue-700'
												: 'bg-emerald-50 text-emerald-700'
										}`}
									>
										{vt.source === 'handover' ? 'Výdej' : 'Vratka'}
									</span>
									{vt.duration_seconds ? (
										<span>{Math.round(vt.duration_seconds)} s</span>
									) : null}
								</div>
								<audio
									src={vt.url}
									controls
									preload="none"
									className="w-full h-8 mb-1"
								/>
								<p className="text-sm text-slate-700 italic">„{vt.transcript}"</p>
							</div>
						))}
					</div>
				</Paper>
			) : null}

			{damageCount === 0 ? (
				<Paper className="p-4 border-emerald-200 bg-emerald-50">
					<p className="text-sm text-emerald-800">
						✓ Žádná nová poškození nebyla zjištěna.
					</p>
				</Paper>
			) : (
				<>
					{hasPhotos ? (
						<div className="flex items-center justify-between text-xs text-slate-500">
							<span>
								{hasBboxes
									? 'Klikni na poškození pro označení na fotce, nebo na rámeček pro detail.'
									: 'Foto-viewer dostupný; model však nevrátil přesné rámečky poškození.'}
							</span>
							<button
								type="button"
								onClick={() => setShowViewer(v => !v)}
								className="rounded-md border border-slate-200 bg-white px-2 py-1 hover:bg-slate-50"
							>
								{showViewer ? 'Skrýt fotky' : 'Zobrazit fotky'}
							</button>
						</div>
					) : null}
					{hasPhotos && showViewer ? (
						<DamagePhotoViewer
							report={report}
							hoveredDamageIdx={hoveredIdx}
							activeDamageIdx={activeIdx}
							onBoxClick={idx => setActiveIdx(prev => (prev === idx ? null : idx))}
							onBoxHover={setHoveredIdx}
						/>
					) : null}
					<div className="space-y-2">
						{damages.map((d, idx) => (
							<DamageRow
								key={idx}
								damage={d}
								index={idx}
								hoveredIdx={hoveredIdx}
								activeIdx={activeIdx}
								onHover={setHoveredIdx}
								onClick={() => setActiveIdx(prev => (prev === idx ? null : idx))}
								feedback={feedbackState[idx]}
								isFeedbackPending={feedback.isPending}
								onFeedback={(action, reason) => {
									setFeedbackState(prev => ({ ...prev, [idx]: action }))
									feedback.mutate(
										{
											damageIndex: idx,
											action,
											reason,
											damageSnapshot: d,
											activityLogId: report.activity_log_id,
										},
										{
											onError: () => {
												setFeedbackState(prev => {
													// Rebuild without the failed idx (avoid dynamic delete).
													return Object.fromEntries(
														Object.entries(prev).filter(
															([k]) => Number(k) !== idx,
														),
													) as Record<number, 'reject' | 'confirm'>
												})
											},
										},
									)
								}}
							/>
						))}
					</div>
				</>
			)}

			{/* Dočasný debug panel — co proběhlo přes Gemini API. */}
			{report.debug ? <DebugPanel debug={report.debug} /> : null}
		</>
	)
}

function DamageRow({
	damage,
	index,
	hoveredIdx,
	activeIdx,
	onHover,
	onClick,
	feedback,
	isFeedbackPending,
	onFeedback,
}: {
	damage: DamageItem
	index: number
	hoveredIdx: number | null
	activeIdx: number | null
	onHover: (idx: number | null) => void
	onClick: () => void
	feedback?: 'reject' | 'confirm'
	isFeedbackPending: boolean
	onFeedback: (action: 'reject' | 'confirm', reason?: string) => void
}) {
	const sev = damage.severity
	const conf = Math.round(damage.confidence * 100)
	const angleLabel = damage.angle ? ANGLE_LABEL[damage.angle] : ''
	const isHovered = hoveredIdx === index
	const isActive = activeIdx === index
	const isRejected = feedback === 'reject'
	const isConfirmed = feedback === 'confirm'
	const ring = isActive ? 'ring-2 ring-blue-500' : isHovered ? 'ring-2 ring-blue-200' : 'ring-0'
	const muted = isRejected ? 'opacity-50 grayscale' : ''

	const [showRejectInput, setShowRejectInput] = useState(false)
	const [reason, setReason] = useState('')

	return (
		<Paper
			className={`p-4 flex items-start gap-3 transition hover:bg-slate-50 ${ring} ${muted}`}
			onMouseEnter={() => onHover(index)}
			onMouseLeave={() => onHover(null)}
		>
			<button
				type="button"
				onClick={onClick}
				aria-label="Zvýraznit na fotce"
				className="flex items-start gap-3 flex-1 min-w-0 text-left cursor-pointer"
			>
				<span className="rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-xxs font-bold tabular-nums">
					#{index + 1}
				</span>
				<span
					className={`rounded-full px-2 py-0.5 text-xxs font-bold uppercase ${SEVERITY_CHIP[sev]}`}
				>
					{TYPE_LABEL[damage.type]}
				</span>
				<div className="flex-1 min-w-0">
					<div className="font-medium text-slate-800">
						{damage.location || '(neuvedeno)'}
						{angleLabel ? (
							<span className="ml-2 text-xs text-slate-400 font-normal">
								· {angleLabel}
							</span>
						) : null}
						{damage.photo_index ? (
							<span className="ml-2 text-xxs text-slate-400">
								foto #{damage.photo_index}
							</span>
						) : null}
					</div>
					{damage.note ? (
						<div className="mt-1 text-sm text-slate-600">{damage.note}</div>
					) : null}
					<div className="mt-1 text-xs text-slate-400">
						závažnost {SEVERITY_LABEL[sev]} ({sev}/3) · jistota {conf}%
						{damage.bbox ? null : (
							<span className="ml-2 text-amber-600">⚠ bez bbox</span>
						)}
					</div>
				</div>
			</button>
			<div className="shrink-0 flex flex-col gap-1 ml-2">
				<button
					type="button"
					title="Potvrdit jako reálné poškození"
					disabled={isFeedbackPending}
					onClick={e => {
						e.stopPropagation()
						onFeedback('confirm')
					}}
					className={`px-2 py-1 text-xs rounded-md border transition ${
						isConfirmed
							? 'bg-emerald-100 border-emerald-300 text-emerald-700'
							: 'border-slate-200 hover:bg-emerald-50 hover:border-emerald-200 text-slate-500'
					}`}
				>
					✓ Potvrdit
				</button>
				<button
					type="button"
					title="Toto poškození tam není"
					disabled={isFeedbackPending}
					onClick={e => {
						e.stopPropagation()
						setShowRejectInput(v => !v)
					}}
					className={`px-2 py-1 text-xs rounded-md border transition ${
						isRejected
							? 'bg-rose-100 border-rose-300 text-rose-700'
							: 'border-slate-200 hover:bg-rose-50 hover:border-rose-200 text-slate-500'
					}`}
				>
					✗ Nesouhlasím
				</button>
				{showRejectInput && !isRejected ? (
					<div
						className="absolute z-10 mt-12 -ml-32 w-64 rounded-lg border border-slate-200 bg-white p-2 shadow-lg"
						onClick={e => e.stopPropagation()}
					>
						<label className="block text-xxs font-semibold text-slate-500 mb-1">
							Důvod (volitelně)
						</label>
						<input
							type="text"
							value={reason}
							onChange={e => setReason(e.target.value)}
							placeholder="např. To tam bylo už při výdeji"
							className="w-full border border-slate-300 rounded px-2 py-1 text-sm"
						/>
						<div className="flex gap-1 mt-2">
							<button
								type="button"
								onClick={() => {
									setShowRejectInput(false)
									onFeedback('reject', reason || undefined)
								}}
								className="flex-1 bg-rose-600 text-white text-xs rounded px-2 py-1 hover:bg-rose-700"
							>
								Odmítnout
							</button>
							<button
								type="button"
								onClick={() => setShowRejectInput(false)}
								className="px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 rounded"
							>
								Zrušit
							</button>
						</div>
					</div>
				) : null}
			</div>
		</Paper>
	)
}

function formatDateTime(iso: string) {
	try {
		const d = new Date(iso)
		return d.toLocaleString('cs-CZ', {
			day: 'numeric',
			month: 'numeric',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		})
	} catch {
		return iso
	}
}

// ---------------------------------------------------------------------------
// DOČASNÝ debug panel.
//
// Zobrazuje, co přesně proběhlo přes Gemini API při posledním běhu analýzy —
// stažení a zmenšení každé fotky, velikost odeslaného payloadu, jednotlivé
// pokusy o modely/klíče a jejich výsledek. Slouží k doladění integrace
// (časté GEMINI_ALL_MODELS_FAILED, nejistota jestli se porovnaly všechny
// fotky). Až bude integrace stabilní, smaž tuto komponentu, oba její callsity
// výše a `debug` blok v server/service/damage-analysis-service.js.
// ---------------------------------------------------------------------------

function formatBytes(n: number | undefined): string {
	if (!n || n <= 0) return '–'
	if (n < 1024) return `${n} B`
	if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} kB`
	return `${(n / 1024 / 1024).toFixed(2)} MB`
}

const PHOTO_STATUS: Record<string, { label: string; cls: string }> = {
	ok: { label: 'OK', cls: 'text-emerald-700 bg-emerald-50' },
	too_large: { label: 'příliš velká', cls: 'text-amber-700 bg-amber-50' },
	http_error: { label: 'HTTP chyba', cls: 'text-red-700 bg-red-50' },
	fetch_error: { label: 'nestáhla se', cls: 'text-red-700 bg-red-50' },
	unresolved: { label: 'neznámá URL', cls: 'text-red-700 bg-red-50' },
	pending: { label: '?', cls: 'text-slate-600 bg-slate-100' },
}

function PhotoDebugTable({
	title,
	rows,
}: {
	title: string
	rows: DamageDebug['photos']['before']
}) {
	return (
		<div className="min-w-0 flex-1">
			<div className="text-xxs font-semibold uppercase tracking-wide text-slate-400 mb-1">
				{title} ({rows.length})
			</div>
			{rows.length === 0 ? (
				<div className="text-xs text-slate-400">žádné fotky</div>
			) : (
				<div className="overflow-x-auto">
					<table className="w-full text-xxs">
						<thead>
							<tr className="text-slate-400 text-left">
								<th className="pr-2 font-medium">#</th>
								<th className="pr-2 font-medium">stav</th>
								<th className="pr-2 font-medium">HTTP</th>
								<th className="pr-2 font-medium">velikost</th>
								<th className="pr-2 font-medium">typ</th>
								<th className="pr-2 font-medium">zmenšeno</th>
								<th className="pr-2 font-medium">rozměry</th>
							</tr>
						</thead>
						<tbody className="text-slate-600">
							{rows.map(p => {
								const st = PHOTO_STATUS[p.status] ?? PHOTO_STATUS.pending
								return (
									<tr key={p.index} className="border-t border-slate-100">
										<td className="pr-2 py-0.5 tabular-nums">{p.index + 1}</td>
										<td className="pr-2 py-0.5">
											<span className={`rounded px-1 ${st.cls}`}>
												{st.label}
											</span>
										</td>
										<td className="pr-2 py-0.5 tabular-nums">
											{p.http_status ?? '–'}
										</td>
										<td className="pr-2 py-0.5 tabular-nums">
											{formatBytes(p.final_bytes ?? p.bytes)}
										</td>
										<td className="pr-2 py-0.5">
											{p.final_mime ?? p.source_mime ?? '–'}
										</td>
										<td className="pr-2 py-0.5">
											{p.resized ? 'ano' : p.status === 'ok' ? 'ne' : '–'}
										</td>
										<td className="pr-2 py-0.5 tabular-nums">
											{p.width && p.height ? `${p.width}×${p.height}` : '–'}
										</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			)}
		</div>
	)
}

function DebugPanel({ debug }: { debug: DamageDebug }) {
	const ph = debug.photos
	const allPhotosOk =
		ph.before.every(p => p.status === 'ok') && ph.after.every(p => p.status === 'ok')

	return (
		<Paper className="p-4 border-dashed border-slate-300 bg-slate-50">
			<details>
				<summary className="cursor-pointer text-sm font-bold text-slate-600 select-none">
					🔧 Debug (dočasné) — co proběhlo přes Gemini API
				</summary>
				<p className="mt-1 mb-3 text-xxs text-slate-400">
					{debug.note} Po doladění integrace lze celý tento panel odstranit.
				</p>

				{/* Souhrn */}
				<div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-3">
					<div>
						<div className="text-slate-400 text-xxs uppercase">Celkem</div>
						<div className="text-slate-700 tabular-nums">
							{(debug.total_duration_ms / 1000).toFixed(1)} s
						</div>
					</div>
					<div>
						<div className="text-slate-400 text-xxs uppercase">Payload</div>
						<div className="text-slate-700 tabular-nums">
							{debug.payload
								? `${formatBytes(debug.payload.estimated_bytes)} / ${
										debug.payload.image_part_count
									} fotek`
								: '– (nedošlo k sestavení)'}
						</div>
					</div>
					<div>
						<div className="text-slate-400 text-xxs uppercase">sharp (resize)</div>
						<div
							className={debug.sharp_available ? 'text-emerald-700' : 'text-red-700'}
						>
							{debug.sharp_available ? 'dostupný' : 'CHYBÍ – fotky se nezmenšují!'}
						</div>
					</div>
					<div>
						<div className="text-slate-400 text-xxs uppercase">API klíče</div>
						<div className="text-slate-700 tabular-nums">{debug.config.key_count}</div>
					</div>
				</div>

				{/* Fotky */}
				<div className="mb-3">
					<div className="text-xs text-slate-500 mb-1">
						Porovnáno <strong>{ph.paired}</strong> párů úhlů · staženo{' '}
						{ph.before_loaded}/{ph.before_total} z výdeje, {ph.after_loaded}/
						{ph.after_total} z vratky.
						{allPhotosOk ? (
							<span className="ml-1 text-emerald-600">
								Všechny fotky staženy v pořádku.
							</span>
						) : (
							<span className="ml-1 text-amber-600">
								Některé fotky se nepodařilo zpracovat — viz tabulka (chybějící úhel
								se z porovnání vynechá, ostatní páry zůstávají správně spárované).
							</span>
						)}
					</div>
					<div className="flex flex-col sm:flex-row gap-4">
						<PhotoDebugTable title="Výdej (PŘED)" rows={ph.before} />
						<PhotoDebugTable title="Vratka (PO)" rows={ph.after} />
					</div>
				</div>

				{/* Pokusy o modely */}
				<div className="mb-3">
					<div className="text-xxs font-semibold uppercase tracking-wide text-slate-400 mb-1">
						Pokusy o modely ({debug.attempts.length})
					</div>
					{debug.attempts.length === 0 ? (
						<div className="text-xs text-slate-400">žádné volání API neproběhlo</div>
					) : (
						<ul className="space-y-0.5 text-xs">
							{debug.attempts.map((a, i) => {
								const ok = a.status >= 200 && a.status < 300
								return (
									<li key={i} className="flex flex-wrap items-baseline gap-x-2">
										<code className="text-slate-700">{a.model}</code>
										<span className="text-slate-400">klíč #{a.key_index}</span>
										<span
											className={
												ok ? 'text-emerald-700' : 'text-red-700 font-medium'
											}
										>
											→ {a.status || 'síťová chyba'}
										</span>
										{a.reason ? (
											<span className="text-slate-500">{a.reason}</span>
										) : null}
										{a.duration_ms ? (
											<span className="text-slate-300 tabular-nums">
												{(a.duration_ms / 1000).toFixed(1)} s
											</span>
										) : null}
										{a.message ? (
											<span className="w-full text-xxs text-slate-400 pl-2">
												{a.message}
											</span>
										) : null}
									</li>
								)
							})}
						</ul>
					)}
					{debug.used ? (
						<div className="mt-1 text-xs text-emerald-700">
							✓ Použit model <code>{debug.used.model}</code> (klíč #
							{debug.used.key_index}
							{debug.used.model_fallback_used ? ', fallback' : ''})
						</div>
					) : (
						<div className="mt-1 text-xs text-red-700">✗ Žádný model neuspěl.</div>
					)}
				</div>

				{/* Payload shrink + hlasové poznámky + model chain */}
				{debug.payload && debug.payload.shrink_passes.length > 0 ? (
					<div className="mb-2 text-xs text-amber-700">
						Payload byl nad limit ({formatBytes(debug.payload.limit_bytes)}), proto
						zmenšen:{' '}
						{debug.payload.shrink_passes
							.map(s => `${s.target_dim}px → ${formatBytes(s.estimated_bytes)}`)
							.join(', ')}
						.
					</div>
				) : null}
				<div className="text-xxs text-slate-400">
					Model chain: {debug.config.model_primary} →{' '}
					{debug.config.model_chain.join(' → ')} · multi-pass:{' '}
					{debug.config.multi_pass ? 'zapnuto' : 'vypnuto'} · hlasové poznámky:{' '}
					{debug.voice_notes.transcribed}/{debug.voice_notes.found} přepsáno · limit{' '}
					{debug.config.max_photos} fotek / {debug.config.max_dim_px} px
				</div>
			</details>
		</Paper>
	)
}
